import api from "./axios";

// Admin dashboard API — all endpoints require a Django staff user (is_staff).
// The React AdminShell mirrors this gate client-side; the backend
// IsAdminUser permission is the real guard.

// --- Overview ------------------------------------------------------------

export const getAdminStats = async () => {
  const res = await api.get("/api/admin/stats/");
  return res.data;
};

// --- Users ---------------------------------------------------------------

export const listUsers = async ({ q = "", page = 1 } = {}) => {
  const params = { page };
  if (q) params.q = q;
  const res = await api.get("/api/admin/users/", { params });
  return res.data; // { count, page, page_size, results }
};

// patch: { tier?, is_active?, is_staff? }
export const updateUser = async (id, patch) => {
  const res = await api.patch(`/api/admin/users/${id}/`, patch);
  return res.data;
};

// --- Support tickets (Contact Support submissions) -----------------------

export const listContactMessages = async ({ page = 1, status = "" } = {}) => {
  const params = { page };
  if (status) params.status = status;
  const res = await api.get("/api/admin/contact-messages/", { params });
  return res.data; // { count, page, page_size, open_count, results }
};

// patch: { status: "new" | "in_progress" | "resolved" }
export const updateContactMessage = async (id, patch) => {
  const res = await api.patch(`/api/admin/contact-messages/${id}/`, patch);
  return res.data;
};

// --- Advisor review queue ------------------------------------------------

export const listAdvisors = async (reviewStatus = "") => {
  const params = {};
  if (reviewStatus) params.review_status = reviewStatus;
  const res = await api.get("/api/admin/advisors/", { params });
  return res.data;
};

export const approveAdvisor = async (id) => {
  const res = await api.post(`/api/admin/advisors/${id}/approve/`);
  return res.data;
};

export const rejectAdvisor = async (id) => {
  const res = await api.post(`/api/admin/advisors/${id}/reject/`);
  return res.data;
};

// --- Platform plan catalog ----------------------------------------------

export const listPlatformPlans = async () => {
  const res = await api.get("/api/admin/plans/");
  return res.data;
};

export const createPlatformPlan = async (payload) => {
  const res = await api.post("/api/admin/plans/", payload);
  return res.data;
};

export const updatePlatformPlan = async (id, patch) => {
  const res = await api.patch(`/api/admin/plans/${id}/`, patch);
  return res.data;
};

export const deletePlatformPlan = async (id) => {
  const res = await api.delete(`/api/admin/plans/${id}/`);
  return res.data;
};

// --- Research Desk (knowledge agents that feed the Council) ---------------

export const listResearchAgents = async () => {
  const res = await api.get("/api/admin/research/agents/");
  return res.data;
};

export const getResearchAgent = async (key) => {
  const res = await api.get(`/api/admin/research/agents/${key}/`);
  return res.data;
};

export const updateResearchAgent = async (key, patch) => {
  const res = await api.patch(`/api/admin/research/agents/${key}/`, patch);
  return res.data;
};

export const createResearchAgent = async (payload) => {
  const res = await api.post("/api/admin/research/agents/", payload);
  return res.data;
};

// body: { jurisdiction?, source_url?, source_title?, note? }
export const runResearchAgent = async (key, body = {}) => {
  const res = await api.post(`/api/admin/research/agents/${key}/run/`, body);
  return res.data;
};

export const listKnowledgeItems = async ({ agent = "", status = "", jurisdiction = "" } = {}) => {
  const params = {};
  if (agent) params.agent = agent;
  if (status) params.status = status;
  if (jurisdiction) params.jurisdiction = jurisdiction;
  const res = await api.get("/api/admin/research/items/", { params });
  return res.data;
};

export const addKnowledgeItem = async (payload) => {
  const res = await api.post("/api/admin/research/items/", payload);
  return res.data;
};

export const approveKnowledgeItem = async (id) => {
  const res = await api.post(`/api/admin/research/items/${id}/approve/`);
  return res.data;
};

export const rejectKnowledgeItem = async (id) => {
  const res = await api.post(`/api/admin/research/items/${id}/reject/`);
  return res.data;
};

export const listResearchRuns = async (agent = "") => {
  const params = {};
  if (agent) params.agent = agent;
  const res = await api.get("/api/admin/research/runs/", { params });
  return res.data;
};

export const listResearchSources = async (agent = "") => {
  const params = {};
  if (agent) params.agent = agent;
  const res = await api.get("/api/admin/research/sources/", { params });
  return res.data;
};

export const getResearchBrief = async (force = false) => {
  const params = force ? { force: 1 } : {};
  const res = await api.get("/api/admin/research/brief/", { params });
  return res.data;
};

export const getResearchOps = async () => {
  const res = await api.get("/api/admin/research/ops/");
  return res.data;
};
